/**
 * MyProfilePage — V2 orchestrator for the logged-in user's account page.
 *
 * Replaces the V1 ProfileContent.jsx (579 lines, Bootstrap modals, DOM
 * manipulation). Single-page layout with two sections:
 *   - Account Information (read-only field grid)
 *   - Security (password reset action)
 *
 * Edit flows:
 *   - EditProfileDrawer — email, display name (if permitted), groups (if permitted)
 *   - ResetSelfPasswordDialog — current + new + confirm password
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './my-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../servers/profile/components/ProfileBanner.jsx';
import EditProfileDrawer from './components/EditProfileDrawer.jsx';
import ResetSelfPasswordDialog from './components/ResetSelfPasswordDialog.jsx';

import {
  getSession, setSession, getCampusId, getUserId, getSessionId, getCampusConfig,
} from '../../../services/session.js';
import { fetchUserGroups, editSelfProfile, resetSelfPassword } from './api/myProfileApi.js';

// ==========================================================================
// COMPONENT
// ==========================================================================

const MyProfilePage = () => {
  // --------------------------------------------------------------------------
  // SESSION (read once — profile data lives in session, not in API response)
  // --------------------------------------------------------------------------
  const [sessionSnapshot, setSessionSnapshot] = useState(() => getSession());

  const campusId = useMemo(() => getCampusId(), []);
  const userId = useMemo(() => getUserId(), []);
  const clientId = useMemo(() => getSessionId(), []);

  const username     = sessionSnapshot?.username || '';
  const displayName  = sessionSnapshot?.display || '';
  const email        = sessionSnapshot?.email || '';
  const roleName     = sessionSnapshot?.role_name || sessionSnapshot?.userrole || '';
  const roleId       = sessionSnapshot?.role_id;
  const cognitoId    = sessionSnapshot?.cognito || '';
  const groups       = sessionSnapshot?.groups || [];
  const displayEdit  = !!sessionSnapshot?.displayEdit;
  const groupEdit    = !!sessionSnapshot?.groupEdit;
  const migrated     = sessionSnapshot?.migrated || false;

  const getSessionParams = useCallback(() => ({
    campusId,
    userId,
    clientId,
    username,
    roleId,
    cognitoId,
    displayName,
    email,
    groups,
    displayEdit,
    groupEdit,
    migrated,
  }), [campusId, userId, clientId, username, roleId, cognitoId,
       displayName, email, groups, displayEdit, groupEdit, migrated]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // DATA STATE
  // --------------------------------------------------------------------------
  const [groupData, setGroupData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch group data once on mount (for resolving group IDs → names)
  useEffect(() => {
    if (!campusId) { setLoading(false); return; }
    fetchUserGroups(campusId)
      .then((data) => { setGroupData(Array.isArray(data) ? data : []); })
      .catch(() => { setGroupData([]); })
      .finally(() => setLoading(false));
  }, [campusId]);

  // Resolve group IDs to comma-separated names
  const groupNames = useMemo(() => {
    if (!groups.length || !groupData.length) return 'None';
    const nameMap = new Map(groupData.map((g) => [g.group_id, g.group_name]));
    const names = groups
      .map((id) => nameMap.get(id))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return names.length > 0 ? names.join(', ') : 'None';
  }, [groups, groupData]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // BANNER META
  // --------------------------------------------------------------------------
  const bannerMeta = useMemo(() => [
    { label: 'Email', value: email || '\u2014' },
    { label: 'Role', value: roleName || '\u2014' },
  ], [email, roleName]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  /**
   * Save profile edits (email, display name, groups).
   * Updates session after successful API call.
   */
  const handleEditProfile = useCallback(async (formData) => {
    setSaving(true);
    try {
      const result = await editSelfProfile(formData, getSessionParams());
      if (result[0]) {
        // Update encrypted session
        const s = getSession();
        s.email = formData.email.toLowerCase();
        if (displayEdit) s.display = formData.display_name;
        if (groupEdit) s.groups = formData.group_ids;
        setSession(s);

        // Refresh local snapshot
        setSessionSnapshot({ ...s });

        setEditDrawerOpen(false);
        showToast('success', 'Profile saved.');
      } else {
        const code = result[1];
        let msg;
        if (code === '1') {
          msg = 'Save failed \u2014 user lookup failed. Contact your administrator.';
        } else {
          msg = result[1] || 'Save failed. Please try again.';
        }
        showToast('error', msg);
      }
    } catch {
      showToast('error', 'An unexpected error occurred.');
    } finally {
      setSaving(false);
    }
  }, [getSessionParams, displayEdit, groupEdit, showToast]);

  /**
   * Reset own password (requires current password).
   */
  const handleResetSelfPassword = useCallback(async (data) => {
    const result = await resetSelfPassword(data, getSessionParams());
    if (result[0]) {
      setResetDialogOpen(false);
      showToast('success', 'Password changed successfully.');
    }
    return result; // Dialog handles error display internally
  }, [getSessionParams, showToast]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>&#x2715;</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <span className="dp-breadcrumb__current">My Profile</span>
              </nav>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={displayName || username}
          subtitle={username}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Account Information Section */}
        <div className="pp-tab-content">
          <div className="pp-section">
            <div className="pp-section__header">
              <h3 className="pp-section__title">Account Information</h3>
              <div className="pp-section__actions">
                <button
                  className="dp-btn dp-btn--secondary dp-btn--sm"
                  onClick={() => setEditDrawerOpen(true)}
                >
                  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                  </svg>
                  Edit
                </button>
              </div>
            </div>
            <div className="pp-section__body">
              <div className="pp-field-grid">
                <div className="pp-field">
                  <span className="pp-field__label">Username</span>
                  <span className="pp-field__value">{username || '\u2014'}</span>
                </div>
                <div className="pp-field">
                  <span className="pp-field__label">Display Name</span>
                  <span className="pp-field__value">{displayName || '\u2014'}</span>
                </div>
                <div className="pp-field">
                  <span className="pp-field__label">Email</span>
                  <span className="pp-field__value">{email || '\u2014'}</span>
                </div>
                <div className="pp-field">
                  <span className="pp-field__label">Role</span>
                  <span className="pp-field__value">{roleName || '\u2014'}</span>
                </div>
                <div className="pp-field pp-field--full">
                  <span className="pp-field__label">Groups</span>
                  <span className="pp-field__value">{groupNames}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="pp-section">
            <div className="pp-section__header">
              <h3 className="pp-section__title">Security</h3>
            </div>
            <div className="pp-section__body">
              <div className="mpp-security-row">
                <div className="mpp-security-row__info">
                  <span className="mpp-security-row__label">Password</span>
                  <span className="mpp-security-row__hint">
                    Change your password. Minimum 6 characters.
                  </span>
                </div>
                <button
                  className="dp-btn dp-btn--secondary dp-btn--sm"
                  onClick={() => setResetDialogOpen(true)}
                >
                  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Drawer */}
        <EditProfileDrawer
          open={editDrawerOpen}
          onClose={() => setEditDrawerOpen(false)}
          onSave={handleEditProfile}
          saving={saving}
          email={email}
          displayName={displayName}
          groups={groups}
          groupData={groupData}
          displayEdit={displayEdit}
          groupEdit={groupEdit}
        />

        {/* Reset Self Password Dialog */}
        {resetDialogOpen && (
          <ResetSelfPasswordDialog
            username={username}
            onClose={() => setResetDialogOpen(false)}
            onConfirm={handleResetSelfPassword}
          />
        )}
      </div>
      </div>
    </>
  );
};

export default MyProfilePage;
